import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { PauseIcon, PlayIcon } from 'lucide-react';
import { ProductImage } from '../../ui/ProductImage';
import { btnMediaControl } from '../../ui/styles';
import { focusPosition, librarySrcSet } from '../../core/rules/media';
import type { Media } from '../../core/types/media';
type Props = {
  media: Media;
  /**
   * Vorm en maat van het vlak (bijv. `aspect-[4/5] w-full`). Het vlak staat vast vóór het beeld
   * laadt, dus er verspringt niets.
   */
  className?: string;
  /** Hoe breed het beeld op de pagina staat, voor `srcset` (bijv. `(min-width: 1024px) 50vw, 100vw`). */
  sizes?: string;
  /** Grootste beeld boven de vouw: direct en met voorrang laden. */
  priority?: boolean;
  /** Waar de pauzeknop van een video staat. `top-right` = in een hero met een tekstvel over de onderrand. */
  controlsPosition?: ControlsPosition;
};
type ControlsPosition = 'bottom-left' | 'bottom-right' | 'top-right';
const CONTROLS_CLS: Record<ControlsPosition, string> = {
  'bottom-left': 'bottom-3 left-3',
  'bottom-right': 'bottom-3 right-3',
  'top-right': 'right-3 top-3'
};

/** Wil de bezoeker zuinig met data? Dan alleen de poster. */
const saveData = () => typeof navigator !== 'undefined' && Boolean((navigator as Navigator & {
  connection?: {
    saveData?: boolean;
  };
}).connection?.saveData);

/**
 * Eén beeld op de site: een foto, of een stille loop met de foto als poster.
 *
 * - Foto's uit de eigen bibliotheek krijgen een `srcset` (5 breedtes); andere links lopen via
 *   `ProductImage`, met zijn terugval bij hotlink-beveiliging.
 * - Video speelt alleen zonder "minder beweging" en zonder databesparing, altijd zonder geluid,
 *   en heeft een pauzeknop (WCAG 2.2.2). Telefoons krijgen de kleine versie.
 * - Leeg alt = sfeerbeeld: schermlezers slaan het over.
 */
export function MediaView({
  media,
  className = '',
  sizes = '100vw',
  priority = false,
  controlsPosition = 'bottom-right'
}: Props) {
  const reduce = useReducedMotion();
  const position = focusPosition(media.focus);
  const video = media.video && !reduce && !saveData() ? media.video : null;
  const srcSet = librarySrcSet(media.image);
  return <div className={`relative overflow-hidden bg-blush ${className}`} data-id="element-8960" data-mp="244v4spswl14j">
      {video ? <LoopVideo media={media} video={video} position={position} controlsPosition={controlsPosition} data-id="element-8961" data-mp="28vv4u3xbjzb9" /> : srcSet ? <img src={media.image} srcSet={srcSet} sizes={sizes} alt={media.alt} loading={priority ? 'eager' : 'lazy'} decoding="async" {...priority ? {
      fetchpriority: 'high'
    } : {}} style={position ? {
      objectPosition: position
    } : undefined} className="absolute inset-0 h-full w-full object-cover" data-id="element-8962" data-mp="2cworktz0z82k" /> : <PositionedImage media={media} position={position} priority={priority} data-id="element-8963" data-mp="sj6j7mpjoztd" />}
    </div>;
}

/** Elke andere link: via ProductImage, met zijn terugval bij hotlink-beveiliging. */
function PositionedImage({
  media,
  position,
  priority
}: {
  media: Media;
  position?: string;
  priority: boolean;
}) {
  return <ProductImage src={media.image} alt={media.alt} fit="cover" position={position} loading={priority ? 'eager' : 'lazy'} className="absolute inset-0 h-full w-full" data-id="element-8964" data-mp="35z7zyc49h48x" />;
}
function LoopVideo({
  media,
  video,
  position,
  controlsPosition
}: {
  media: Media;
  video: NonNullable<Media['video']>;
  position?: string;
  controlsPosition: ControlsPosition;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const [paused, setPaused] = useState(false);

  // Buiten beeld stilzetten (scheelt batterij); terug in beeld weer verder, tenzij zelf gepauzeerd.
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      if (entry.isIntersecting && !paused) void el.play().catch(() => setPaused(true));else el.pause();
    });
    io.observe(el);
    return () => io.disconnect();
  }, [paused]);
  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    if (el.paused) {
      void el.play().catch(() => undefined);
      setPaused(false);
    } else {
      el.pause();
      setPaused(true);
    }
  };
  return <>
      <video ref={ref} muted loop playsInline autoPlay preload="metadata" poster={video.poster || media.image} aria-label={media.alt || undefined} aria-hidden={media.alt ? undefined : true} style={position ? {
      objectPosition: position
    } : undefined} className="absolute inset-0 h-full w-full object-cover" data-id="element-8965" data-mp="3o45kuph2ea9c">
        {video.small && <source src={video.small} type="video/mp4" media="(max-width: 767px)" data-id="element-8966" data-mp="2qihuomb7co69" />}
        <source src={video.src} type="video/mp4" data-id="element-8967" data-mp="2qkuihg3n5sr8" />
      </video>
      <button type="button" onClick={toggle} aria-label={paused ? 'Video afspelen' : 'Video pauzeren'} className={`${btnMediaControl} ${CONTROLS_CLS[controlsPosition]}`} data-id="element-8968" data-mp="2vidv6bkoxxh2">
        {paused ? <PlayIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-8969" data-mp="2iyp7nok2wn8z" /> : <PauseIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-8970" data-mp="2wxgm5c2usaxn" />}
      </button>
    </>;
}